import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import Icon from '../../components/AppIcon';

const AuthBackground = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #e8e4f8 0%, #d4d8f5 25%, #c8d4f0 50%, #b8cef0 75%, #a8c8ee 100%)' }}>
    <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
      <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{height:'45%'}}>
        <path fill="rgba(100,120,220,0.18)" d="M0,160L60,170.7C120,181,240,203,360,197.3C480,192,600,160,720,154.7C840,149,960,171,1080,181.3C1200,192,1320,192,1380,192L1440,192L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"/>
      </svg>
      <svg className="absolute bottom-0 right-0 w-3/4" viewBox="0 0 800 400" preserveAspectRatio="none" style={{height:'55%'}}>
        <path fill="rgba(80,100,200,0.22)" d="M800,100L700,120C600,140,400,180,200,190C0,200,0,180,0,160L0,400L800,400Z"/>
      </svg>
      <div className="absolute top-8 right-12 opacity-20" style={{color:'#7c6fe0'}}>
        {[...Array(5)]?.map((_,r)=>(
          <div key={r} className="flex space-x-3 mb-3">
            {[...Array(5)]?.map((_,c)=><div key={c} className="w-1.5 h-1.5 rounded-full bg-current"/>)}
          </div>
        ))}
      </div>
    </div>
    <div className="w-full max-w-md relative z-10">
      {children}
    </div>
  </div>
);

const OtpVerifyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setOtpVerifiedState, logout } = useAuth();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  const emailAddress = location?.state?.email_address || '';

  const handleChange = (index, value) => {
    if (!/^[0-9]?$/?.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');
    if (value && index < 5) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !otp?.[index] && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pasted = e?.clipboardData?.getData('text')?.replace(/\D/g, '')?.slice(0, 6);
    const newOtp = [...otp];
    pasted?.split('')?.forEach((char, i) => { if (i < 6) newOtp[i] = char; });
    setOtp(newOtp);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const code = otp?.join('');
    if (code?.length !== 6) { setError('Please enter the complete 6-digit code'); return; }
    setLoading(true);
    try {
      const response = await apiService?.verify2FA({ email_address: emailAddress, code: code });
      const data = response?.data;
      const verified = data?.details?.verified;
      if (data?.status_code === 200 && verified === true) {
        setOtpVerifiedState(true);
        navigate('/dashboard');
      } else {
        setError('Invalid OTP code. Please try again.');
        setOtp(['', '', '', '', '', '']);
        inputRefs?.current?.[0]?.focus();
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Verification failed. Please try again.');
      setOtp(['', '', '', '', '', '']);
      inputRefs?.current?.[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthBackground>
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg"
          style={{ background: 'linear-gradient(135deg, #5b4fcf 0%, #4a6bc4 100%)' }}>
          <Icon name="ShieldCheck" size={30} color="white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Two-Factor Verification</h1>
        <p className="text-gray-500 mt-2 text-sm">Enter the 6-digit code from your authenticator app</p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-8 border border-white/60">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-center space-x-3">
            {otp?.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(index, e?.target?.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-12 h-14 text-center text-xl font-bold border-2 rounded-xl bg-white text-gray-900 focus:outline-none transition-all"
                style={{
                  borderColor: digit ? '#5b4fcf' : '#dde1f5',
                  boxShadow: digit ? '0 0 0 3px rgba(91,79,207,0.15)' : 'none'
                }}
              />
            ))}
          </div>

          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-xl">
              <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || otp?.join('')?.length !== 6}
            className="w-full h-12 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-all"
            style={{ background: 'linear-gradient(135deg, #5b4fcf 0%, #4a6bc4 100%)' }}
          >
            {loading ? (
              <><Icon name="Loader2" size={16} className="animate-spin" /><span>Verifying...</span></>
            ) : (
              <span>Verify Code</span>
            )}
          </button>

          <button
            type="button"
            onClick={() => { logout(); navigate('/login'); }}
            className="w-full text-sm text-gray-500 hover:text-gray-700 text-center transition-colors"
          >
            Back to Login
          </button>
        </form>
      </div>
    </AuthBackground>
  );
};

export default OtpVerifyPage;
